import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Grow,
  Collapse,
  Dialog,
  Paper,
  useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { dashboardService } from '../../services';
import { DashboardStatus, CostOverviewResponse, CostAnalysis } from '../../types';
import { POLLING_CONFIG } from '../../config/polling';
import UniversalBadge from '../common/UniversalBadge';
import { slideIn } from '../../styles/components/animations';
import ContextMenu, { MenuAction } from '../common/ContextMenu';
import { IMAGES } from '../../constants/assets';
import { useTranslation } from 'react-i18next';

// Frontend-Konfiguration für Service-Status (Separation of Concerns)
const getServiceConfig = (t: ReturnType<typeof useTranslation>['t']) => ({
  complete: {
    name: t('common:status.complete'),
    color: '#4CAF50' // Green (WCAG AA)
  },
  analyzing: {
    name: t('common:status.analyzing'),
    color: '#D55E00' // Vermillion/Orange (WCAG AA+)
  },
  pending: {
    name: t('dashboard:status.pending'),
    color: '#0072B2' // Blue (WCAG AA+)
  },
  open: {
    name: t('common:status.open'),
    color: '#B5B5B5' // Dark Grey (WCAG AA)
  },
  failed: {
    name: t('common:status.failed'),
    color: '#EF4444' // Red (WCAG AA)
  },
  qs: {
    name: t('common:status.qsInProgress'),
    color: '#D55E00' // Align with analyzing
  },
  // QS-Status werden dynamisch behandelt
});

// Hilfsfunktion: Prüft ob Status eine aktive Bearbeitung darstellt
const isProcessingStatus = (status: string): boolean => {
  return status === 'pending' || status === 'analyzing' || status === 'qs' || status.startsWith('qs');
};

// Hilfsfunktion für deutsche Kostenformatierung
const formatCostGerman = (cost: number): string => {
  return cost.toFixed(2).replace('.', ',');
};

// Minimalistic card style (Google-style)
const minimalCard = {
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  borderRadius: '8px',
  border: 1, borderColor: 'divider',
  transition: 'box-shadow 150ms ease',
  '&:hover': {
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
  }
};



interface DashboardSectionProps {
  pollingInterval?: number; // in Millisekunden, Standard: 30 Sekunden
  enablePolling?: boolean; // kann von außen gesteuert werden
}

const DashboardSection: React.FC<DashboardSectionProps> = ({
  pollingInterval = POLLING_CONFIG.DASHBOARD_INTERVAL,
  enablePolling = POLLING_CONFIG.ENABLE_POLLING
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState<DashboardStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [pollingDisabled, setPollingDisabled] = useState<boolean>(false);

  // Kostenübersicht States
  const [costData, setCostData] = useState<CostOverviewResponse | null>(null);
  const [costLoading, setCostLoading] = useState(true);
  const [costError, setCostError] = useState<string | null>(null);
  const [downloadingAnalysis, setDownloadingAnalysis] = useState<string | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [isCostPolling, setIsCostPolling] = useState(false);
  const [costErrorCount, setCostErrorCount] = useState<number>(0);
  const [costPollingDisabled, setCostPollingDisabled] = useState<boolean>(false);
  const [newAnalysisIds, setNewAnalysisIds] = useState<Set<string>>(new Set());

  // Filter-States für Kostenübersicht
  const [zeitraumFilter, setZeitraumFilter] = useState<string>('woche');
  const [dienstleisterFilter, setDienstleisterFilter] = useState<string>('alle');
  const [vonDatum, setVonDatum] = useState<string>('');
  const [bisDatum, setBisDatum] = useState<string>('');

  // Sortierung States
  const [sortColumn, setSortColumn] = useState<string>('datum');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Context Menu States
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    analysisId: string;
  } | null>(null); // Default: neueste zuerst

  // Dashboard-Daten laden
  const loadDashboardData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setIsPolling(true);

      const data = await dashboardService.getDashboardStatus();

      // 🔧 PERFORMANCE: Nur State updaten wenn sich Daten wirklich geändert haben
      setDashboardData(prevData => {
        if (!prevData) return data;

        // Vergleiche wichtige Felder
        const hasChanges =
          prevData.total !== data.total ||
          JSON.stringify(prevData.statusCounts) !== JSON.stringify(data.statusCounts);

        return hasChanges ? data : prevData;
      });

      setError(null);
      setErrorCount(0); // Reset bei erfolgreichem Request
    } catch (err: any) {
      console.error('Fehler beim Laden der Dashboard-Daten:', err);

      // Fehler-Counter erhöhen
      const newErrorCount = errorCount + 1;
      setErrorCount(newErrorCount);

      // Bei wiederholten Fehlern (Network Errors, 403, etc.): Polling deaktivieren
      if (newErrorCount >= 3 ||
        err?.code === 'ERR_NETWORK' ||
        err?.code === 'ERR_CONNECTION_REFUSED' ||
        err?.response?.status === 403) {
        setPollingDisabled(true);
        console.log('🚫 Dashboard Polling deaktiviert nach wiederholten Fehlern');
      }

      setError(t('dashboard:errors.loadError'));
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, [errorCount]);

  // Initiales Laden
  useEffect(() => {
    loadDashboardData(true);
  }, [loadDashboardData]);



  // ⚡ GLOBALES POLLING SYSTEM ⚡
  useEffect(() => {
    if (!enablePolling || pollingDisabled) return;

    // Registriere Dashboard-Komponente beim globalen Polling-Manager
    const { PollingManager } = require('../../config/polling');
    const pollingManager = PollingManager.getInstance();

    // Wrapper-Funktion für Dashboard-Polling
    const pollingCallback = async () => {
      if (!isPolling && !pollingDisabled) {
        await loadDashboardData(false); // Kein Loading-Spinner beim Polling
      }
    };

    // Registriere bei globalem Manager
    pollingManager.register('dashboard-main', pollingCallback);

    // Legacy-Fallback für individuelle Intervalle (falls globales System deaktiviert)
    const legacyInterval = !POLLING_CONFIG.ENABLE_POLLING ? setInterval(() => {
      if (!isPolling && !pollingDisabled) {
        loadDashboardData(false);
      }
    }, pollingInterval) : null;

    return () => {
      // Cleanup: Deregistriere vom globalen Manager
      pollingManager.unregister('dashboard-main');

      // Legacy-Cleanup
      if (legacyInterval) {
        clearInterval(legacyInterval);
      }
    };
  }, [enablePolling, pollingInterval, isPolling, pollingDisabled, loadDashboardData]);



  const handleDownloadDashboard = async () => {
    try {
      setDownloading(true);
      await dashboardService.downloadDashboardExcel();
    } catch (err) {
      console.error('Fehler beim Herunterladen des Dashboards:', err);
      setError(t('dashboard:errors.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  // Kostenübersicht-Daten laden mit Animation-Logik
  const loadCostData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setCostLoading(true);
      }
      setIsCostPolling(true);

      const filters = {
        zeitraum: zeitraumFilter,
        dienstleister: dienstleisterFilter,
        ...(zeitraumFilter === 'custom' && vonDatum && bisDatum && {
          vonDatum,
          bisDatum
        })
      };

      const data = await dashboardService.getCostOverview(filters);

      // 🎬 ANIMATION: Neue Einträge erkennen und animieren
      setCostData(prevData => {
        if (!prevData) {
          return data;
        }

        // Finde neue Analysen durch Vergleich der IDs
        const prevIds = new Set(prevData.analyses.map(a => a.id));
        const newIds = data.analyses
          .filter(a => !prevIds.has(a.id))
          .map(a => a.id);

        if (newIds.length > 0) {
          // Markiere neue IDs für Animation
          setNewAnalysisIds(new Set(newIds));

          // Nach kurzer Zeit Animation entfernen
          setTimeout(() => {
            setNewAnalysisIds(new Set());
          }, 6000);
        }

        return data;
      });

      setCostError(null);
      setCostErrorCount(0);
    } catch (err: any) {
      console.error('Fehler beim Laden der Kostenübersicht:', err);

      // Fehler-Counter erhöhen
      const newErrorCount = costErrorCount + 1;
      setCostErrorCount(newErrorCount);

      // Bei wiederholten Fehlern: Polling deaktivieren
      if (newErrorCount >= 3 ||
        err?.code === 'ERR_NETWORK' ||
        err?.code === 'ERR_CONNECTION_REFUSED' ||
        err?.response?.status === 403) {
        setCostPollingDisabled(true);
        console.log('🚫 Kostenübersicht Polling deaktiviert nach wiederholten Fehlern');
      }

      setCostError(t('dashboard:errors.costLoadError'));
    } finally {
      setCostLoading(false);
      setIsCostPolling(false);
    }
  }, [zeitraumFilter, dienstleisterFilter, vonDatum, bisDatum, costErrorCount]);

  // Kostenübersicht initial und bei Filter-Änderungen laden
  useEffect(() => {
    loadCostData(true);
  }, [loadCostData]);

  // ⚡ KOSTENÜBERSICHT POLLING ⚡
  useEffect(() => {
    if (!enablePolling || costPollingDisabled) return;

    // Registriere Kostenübersicht beim globalen Polling-Manager
    const { PollingManager } = require('../../config/polling');
    const pollingManager = PollingManager.getInstance();

    // Wrapper-Funktion für Kostenübersicht-Polling
    const costPollingCallback = async () => {
      if (!isCostPolling && !costPollingDisabled) {
        await loadCostData(false); // Kein Loading-Spinner beim Polling
      }
    };

    // Registriere bei globalem Manager
    pollingManager.register('dashboard-cost-overview', costPollingCallback);

    return () => {
      // Cleanup: Deregistriere vom globalen Manager
      pollingManager.unregister('dashboard-cost-overview');
    };
  }, [enablePolling, isCostPolling, costPollingDisabled, loadCostData]);

  // Context Menu Handler
  const handleContextMenu = (event: React.MouseEvent, analysisId: string) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
          analysisId,
        }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Download-Funktionen für Analysen
  const handleDownloadResults = async (analysisId: string) => {
    try {
      setDownloadingAnalysis(analysisId);
      // Verwende die bestehende downloadAnalysis Methode für Ergebnisse
      await dashboardService.downloadAnalysis(analysisId);
      handleCloseContextMenu();
    } catch (err: any) {
      console.error('Fehler beim Herunterladen der Ergebnisse:', err);
      setError(t('dashboard:errors.resultsDownloadError'));
    } finally {
      setDownloadingAnalysis(null);
    }
  };

  const handleDownloadAddendum = async (analysisId: string) => {
    try {
      setDownloadingAnalysis(analysisId);
      // TODO: Implementiere downloadAnalysisAddendum in dashboardService
      // Für jetzt verwenden wir die gleiche Methode
      await dashboardService.downloadAddendum(analysisId);
      handleCloseContextMenu();
    } catch (err: any) {
      console.error('Fehler beim Herunterladen der Ergänzungsvereinbarung:', err);
      setError(t('dashboard:errors.addendumDownloadError'));
    } finally {
      setDownloadingAnalysis(null);
    }
  };

  // CSV-Export für gefilterte Daten
  const handleExportCSV = () => {
    const sortedData = getSortedAnalyses();

    // CSV-Header
    const headers = [
      t('dashboard:csv.headers.analysisId'),
      t('dashboard:csv.headers.provider'),
      t('dashboard:csv.headers.service'),
      t('dashboard:csv.headers.criticality'),
      t('dashboard:csv.headers.completed'),


    ];

    // CSV-Daten erstellen
    const csvData = sortedData.map(analysis => {
      const row = [
        analysis.id,
        analysis.dienstleister,
        analysis.dienstleistung,
        analysis.isCritical ? t('dashboard:csv.critical') : t('dashboard:csv.simple'),
        analysis.duration?.completed_at,


      ];



      return row;
    });

    // CSV-String erstellen
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download initiieren
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `DORA_Analysen_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Bulk-Download für alle gefilterten Analysen als ZIP
  const handleBulkDownload = async () => {
    try {
      setBulkDownloading(true);
      const sortedData = getSortedAnalyses();

      if (sortedData.length === 0) {
        setError(t('dashboard:errors.noAnalysesToDownload'));
        return;
      }

      // Echte API-Call für Bulk-Download
      await dashboardService.downloadBulkAnalyses(sortedData.map(a => a.id));


    } catch (err: any) {
      console.error('Fehler beim Bulk-Download:', err);
      setError(t('dashboard:errors.zipCreationError'));
    } finally {
      setBulkDownloading(false);
    }
  };

  // Detail-Dialog State
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<CostAnalysis | null>(null);

  // Detail-Dialog Handler
  const handleShowDetails = (analysisId: string) => {
    const analysis = getSortedAnalyses().find(a => a.id === analysisId);
    if (analysis) {
      setSelectedAnalysis(analysis);
      setDetailDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedAnalysis(null);
  };

  // Context Menu Actions
  const getAnalysisContextMenuActions = (analysisId: string): MenuAction[] => [
    {
      label: t('dashboard:contextMenu.showDetails'),
      icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt={t('dashboard:contextMenu.showDetails')} />,
      onClick: () => handleShowDetails(analysisId)
    },
    {
      label: t('dashboard:contextMenu.downloadResults'),
      icon: <img src={IMAGES.DOWNLOAD} alt={t('dashboard:contextMenu.downloadResults')} />,
      onClick: () => handleDownloadResults(analysisId),
      disabled: downloadingAnalysis === analysisId
    },
    // {
    //   label: 'Ergänzungsvereinbarung herunterladen',
    //   icon: <img src={IMAGES.DOWNLOAD} alt="Download" />,
    //   onClick: () => handleDownloadAddendum(analysisId),
    //   disabled: downloadingAnalysis === analysisId
    // }
  ];

  // Hilfsfunktionen für Kostenübersicht
  const getUniqueDisentleister = () => {
    if (!costData) return [];
    const unique = new Set(costData.analyses.map(analyse => analyse.dienstleister));
    return Array.from(unique);
  };

  // Sortierungsfunktion
  const handleSort = (column: string) => {
    console.log(`🔄 Sortierung: ${column} ${sortColumn === column ? (sortDirection === 'asc' ? '→ desc' : '→ asc') : '(neue Spalte)'}`);

    if (sortColumn === column) {
      // Gleiche Spalte: Richtung umkehren
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Neue Spalte: Default desc für Datum, asc für andere
      setSortColumn(column);
      setSortDirection(column === 'datum' ? 'desc' : 'asc');
    }
  };

  // Sortiere Analysen basierend auf aktueller Sortierung
  const getSortedAnalyses = () => {
    if (!costData || !costData.analyses) return [];

    const sortedAnalyses = [...costData.analyses].sort((a, b) => {
      let valueA, valueB;

      switch (sortColumn) {
        case 'dienstleister':
          valueA = a.dienstleister.toLowerCase();
          valueB = b.dienstleister.toLowerCase();
          break;
        case 'dienstleistung':
          valueA = a.dienstleistung.toLowerCase();
          valueB = b.dienstleistung.toLowerCase();
          break;
        case 'datum':
          // Parse deutsches Datumsformat für Sortierung
          const parseDateGerman = (item: any) => {
            try {
              // IMMER das deutsche Datum mit Uhrzeit verwenden für genaue Sortierung
              // Beispiel: "27.06.2025 13:00" oder "27.06.2025 09:19"
              const cleanStr = item.datum.replace(' Uhr', '').trim(); // Backwards compatibility
              const parts = cleanStr.split(' ');
              const datePart = parts[0]; // "27.06.2025"
              const timePart = parts[1] || '00:00'; // "13:00" oder "00:00"

              const [day, month, year] = datePart.split('.').map((x: string) => parseInt(x.trim()));
              const [hours, minutes] = timePart.split(':').map((x: string) => parseInt(x.trim()));

              const date = new Date(year, month - 1, day, hours || 0, minutes || 0);

              if (isNaN(date.getTime())) {
                console.warn(`⚠️ Ungültiges Datum: "${item.datum}"`);
                return new Date(0); // Fallback für ungültige Daten
              }

              return date;
            } catch (error) {
              console.error(`❌ Datum-Parsing-Fehler für "${item.datum}":`, error);
              return new Date(0); // Fallback für Fehler
            }
          };

          valueA = parseDateGerman(a);
          valueB = parseDateGerman(b);

          break;
        case 'kosten':
          valueA = a.kostenNumerisch || 0;
          valueB = b.kostenNumerisch || 0;
          break;
        case 'kriticalitat':
          // Kritisch vor nicht-kritisch sortieren (true = 1, false = 0)
          valueA = a.isCritical ? 1 : 0;
          valueB = b.isCritical ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (valueA < valueB) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sortedAnalyses;
  };

  // Sortierbare Header-Komponente
  const SortableHeader = ({ column, children }: { column: string, children: React.ReactNode }) => (
    <TableCell
      sx={{
        fontWeight: 600,
        color: 'text.primary',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
          backgroundColor: 'grey.50'
        }
      }}
      onClick={() => handleSort(column)}
      data-testid={`dashboard-costs-table-header-${column}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {children}
        {sortColumn === column && (
          sortDirection === 'asc' ?
            <ArrowUpwardIcon fontSize="small" /> :
            <ArrowDownwardIcon fontSize="small" />
        )}
      </Box>
    </TableCell>
  );

  // Nur initiales Loading anzeigen, nicht beim Polling
  if (loading && !dashboardData) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            {t('common:buttons.retry')}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const totalServices = dashboardData.total;
  const serviceConfig = getServiceConfig(t);

  // Transformiere Backend-Daten zu UI-freundlichem Format (nur Werte > 0)
  const chartData = Object.entries(dashboardData.statusCounts)
    .filter(([status, value]) => value > 0)
    .map(([status, value]) => {
      // Standard Status inkl. 'qs' und 'failed'
      return {
        name: serviceConfig[status as keyof typeof serviceConfig]?.name || status,
        value,
        color: serviceConfig[status as keyof typeof serviceConfig]?.color || '#B5B5B5'
      };
    })
    ;

  const chartData2 = Object.entries(dashboardData.statusCounts).map(([status, value]) => ({
    name: serviceConfig[status as keyof typeof serviceConfig]?.name || status,
    value,
    color: serviceConfig[status as keyof typeof serviceConfig]?.color || '#B5B5B5'
  }));
  return (
    <>
      {/* Dashboard - Flexbox statt MUI Grid für bessere Kontrolle */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 3,
        mb: 5
      }}>
        {/* Kreisdiagramm */}
        <Box sx={{ flex: { lg: 1 }, width: { xs: '100%', lg: 'auto' } }}>
          <Card sx={{
            height: '380px',
            ...minimalCard,
            bgcolor: 'background.paper',
            position: 'relative'
          }}>
            <CardContent sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 3
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
              }}>
                <Typography variant="h6" sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '1.125rem'
                }}>
                  {t('dashboard:title.servicesStatus')}
                </Typography>
              </Box>


              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={(entry) => `${entry.value} ${entry.name}`}
                      outerRadius={75}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="#ffffff"
                      strokeWidth={1}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: 1, borderColor: 'divider',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Status Karten */}
        <Box sx={{ flex: { lg: 1 }, width: { xs: '100%', lg: 'auto' } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '380px'
          }}>
            {/* Gesamt Card */}
            <Card sx={{
              height: '60px',
              ...minimalCard,
              bgcolor: 'background.paper'
            }}>
              <CardContent sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '100%',
                p: 2,
                '&:last-child': { pb: 2 }
              }}>
                <Typography variant="h6" sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  {t('dashboard:title.total')}
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.25rem'
                }}>
                  {dashboardData.maxServices ? `${totalServices}/${dashboardData.maxServices}` : totalServices}
                </Typography>
              </CardContent>
            </Card>

            {/* Status Karten Grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              flex: 1
            }}>
              {chartData2.map((service, index) => (
                <Card key={`status-card-${index}`} sx={{
                  ...minimalCard,
                  bgcolor: 'background.paper'
                }}>
                  <CardContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    p: 1.5,
                    '&:last-child': { pb: 1.5 }
                  }}>
                    <Typography variant="body2" sx={{
                      color: service.color,
                      textAlign: 'center',
                      fontSize: '0.8125rem',
                      mb: 0.5,
                      fontWeight: 500
                    }}>
                      {service.name}
                    </Typography>
                    <Typography variant="h4" sx={{
                      fontWeight: 600,
                      color: service.color,
                      fontSize: '1.25rem'
                    }}>
                      {service.value}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dashboard-Bereich Abschluss */}
      <Card sx={{
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        borderRadius: '8px',
        bgcolor: 'background.paper',
        mb: 6
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '1.125rem',
                mb: 0.5
              }}>
                {t('dashboard:title.dashboardExport')}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}>
                {t('dashboard:title.exportDescription')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleDownloadDashboard}
              disabled={downloading || ((dashboardData?.statusCounts.complete ?? 0) === 0)}
              data-testid="dashboard-main-download-excel-button"
              sx={{
                textTransform: 'none',
                minWidth: '200px'
              }}
            >
              {downloading ? t('dashboard:buttons.downloading') : t('dashboard:buttons.downloadFullDashboard')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Trennlinie und neuer Bereich */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        my: 4,
        '&::before, &::after': {
          content: '""',
          flex: 1,
          height: '1px',
          backgroundColor: '#e5e7eb'
        }
      }}>
      </Box>

      {/* Kostenübersicht mit Filtern */}
      <Card sx={{
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        borderRadius: '8px',
        bgcolor: 'background.paper',
        mb: 4
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: 3,
            borderBottom: 1, borderColor: 'divider'
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              mb: 3
            }}>
              <Typography variant="h4" sx={{
                color: 'text.primary',
                mb: 1,
                fontWeight: 400,
                fontSize: '2rem'
              }}>
                {t('dashboard:title.completedAnalysesHistory')}
              </Typography>
            </Box>
          </Box>

          {/* Filter-Bereich */}
          <Box sx={{
            p: 3,
            borderBottom: 1, borderColor: 'divider',
            bgcolor: 'grey.50'
          }}>
            <Grid container spacing={2} alignItems="end">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('dashboard:filters.timeframe')}</InputLabel>
                  <Select
                    value={zeitraumFilter}
                    label={t('dashboard:filters.timeframe')}
                    onChange={(e) => setZeitraumFilter(e.target.value)}
                    data-testid="dashboard-costs-filter-timeframe-select"
                  >
                    <MenuItem value="alle">{t('dashboard:filters.options.allEntries')}</MenuItem>
                    <MenuItem value="heute">{t('dashboard:filters.options.today')}</MenuItem>
                    <MenuItem value="woche">{t('dashboard:filters.options.last7Days')}</MenuItem>
                    <MenuItem value="monat">{t('dashboard:filters.options.currentMonth')}</MenuItem>
                    <MenuItem value="custom">{t('dashboard:filters.options.custom')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('dashboard:filters.provider')}</InputLabel>
                  <Select
                    value={dienstleisterFilter}
                    label={t('dashboard:filters.provider')}
                    onChange={(e) => setDienstleisterFilter(e.target.value)}
                    data-testid="dashboard-costs-filter-provider-select"
                  >
                    <MenuItem value="alle">{t('dashboard:filters.options.allProviders')}</MenuItem>
                    {getUniqueDisentleister().map((anbieter) => (
                      <MenuItem key={anbieter} value={anbieter}>
                        {anbieter}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {zeitraumFilter === 'custom' && (
                <>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('dashboard:filters.dateFrom')}
                      type="date"
                      value={vonDatum}
                      onChange={(e) => setVonDatum(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      data-testid="dashboard-costs-filter-date-from-input"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('dashboard:filters.dateTo')}
                      type="date"
                      value={bisDatum}
                      onChange={(e) => setBisDatum(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      data-testid="dashboard-costs-filter-date-to-input"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          <TableContainer
            sx={{
              maxHeight: '60vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <SortableHeader column="dienstleister">{t('dashboard:table.columns.provider')}</SortableHeader>
                  <SortableHeader column="dienstleistung">{t('dashboard:table.columns.service')}</SortableHeader>
                  <SortableHeader column="datum">{t('dashboard:table.columns.date')}</SortableHeader>
                  <SortableHeader column="kriticalitat">{t('dashboard:table.columns.criticality')}</SortableHeader>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>{t('dashboard:table.columns.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : costError ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#ef4444' }}>
                      {costError}
                    </TableCell>
                  </TableRow>
                ) : costData && costData.analyses.length > 0 ? (
                  getSortedAnalyses().map((analyse, index) => {
                    const isNewAnalysis = newAnalysisIds.has(analyse.id);

                    return (
                      <Grow
                        key={analyse.id || index}
                        in={true}
                        timeout={isNewAnalysis ? 800 : 0}
                        style={{
                          transformOrigin: 'center top',
                          transitionDelay: isNewAnalysis ? `${index * 100}ms` : '0ms'
                        }}
                      >
                        <TableRow
                          hover
                          onContextMenu={(e) => handleContextMenu(e, analyse.id)}
                          sx={{
                            cursor: 'pointer',
                            height: '4.25rem',
                            ...(isNewAnalysis && {
                              animation: `${slideIn} 0.8s ease-out`,
                              backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(92, 124, 250, 0.15)'
                                : '#f0f9ff',
                              borderLeft: theme.palette.mode === 'dark'
                                ? '4px solid #5c7cfa'
                                : '4px solid #0ea5e9',
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? 'rgba(92, 124, 250, 0.25)'
                                  : '#e0f2fe',
                              }
                            }),
                            transition: 'all 1s ease',
                          }}
                          onClick={() => handleShowDetails(analyse.id)}
                          data-testid={`dashboard-analysis-row-${analyse.id}`}
                        >
                          <TableCell sx={{ color: 'text.primary' }}>{analyse.dienstleister}</TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>{analyse.dienstleistung}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{analyse.datum}</TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            <UniversalBadge type="criticality" value={analyse.isCritical ? t('dashboard:csv.critical') : t('dashboard:csv.simple')} />

                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContextMenu(e, analyse.id);
                              }}
                              disabled={downloadingAnalysis === analyse.id}
                              data-testid={`dashboard-analysis-${analyse.id}-menu-button`}
                            >
                              {downloadingAnalysis === analyse.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <MoreVertIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </Grow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      {t('dashboard:table.noResults')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Zusammenfassung und Aktionen unter der Tabelle */}
          <Box sx={{
            p: 3,
            borderTop: 1, borderColor: 'divider',
            bgcolor: 'grey.50'
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}>
              {/* Linke Seite: Statistiken */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3
              }}>
                <Typography variant="body2" sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}>
                  {t('dashboard:table.analysesFound', { count: costData?.filteredCount || 0 })}
                </Typography>
              </Box>

              {/* Rechte Seite: Action Buttons */}
              <Box sx={{
                display: 'flex',
                gap: 2
              }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleExportCSV}
                  disabled={!costData || costData.analyses.length === 0}
                  data-testid="dashboard-costs-export-csv-button"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem'
                  }}
                >
                  {t('dashboard:buttons.csvExport')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBulkDownload}
                  disabled={!costData || costData.analyses.length === 0 || bulkDownloading}
                  data-testid="dashboard-costs-bulk-download-button"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem'
                  }}
                >
                  {bulkDownloading ? t('dashboard:buttons.creatingZip') : t('dashboard:buttons.downloadAllAsZip')}
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Context Menu für Analysen */}
      <ContextMenu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        mouseX={contextMenu?.mouseX || 0}
        mouseY={contextMenu?.mouseY || 0}
        actions={contextMenu ? getAnalysisContextMenuActions(contextMenu.analysisId) : []}
      />

      {/* Detail-Dialog für Analyse */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid #e5e7eb'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('dashboard:title.analysisDetails')}
          </Typography>
          <IconButton
            onClick={handleCloseDetailDialog}
            size="small"
            data-testid="dashboard-analysis-detail-close-button"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>

          {selectedAnalysis && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basis-Informationen */}
              <Box sx={{
                p: 3,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: 1, borderColor: 'divider'
              }}>


                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {t('dashboard:details.analysisId')}
                    </Typography>
                    <Typography variant="body2" sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      wordBreak: 'break-all',
                      bgcolor: 'background.paper',
                      p: 1,
                      borderRadius: 0.5,
                      border: 1, borderColor: 'divider'
                    }}>
                      {selectedAnalysis.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {t('dashboard:details.provider')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {selectedAnalysis.dienstleister}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {t('dashboard:details.service')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {selectedAnalysis.dienstleistung}
                    </Typography>
                  </Grid>
                  {/*  <Grid item xs={6}>
                   <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Gesamt-Kosten
                    </Typography>               
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#059669' }}>
                      {`${formatCostGerman(selectedAnalysis.kostenNumerisch)} €`}
                    </Typography> 
                  </Grid>  */}
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {t('dashboard:details.criticality')}
                    </Typography>
                    <UniversalBadge
                      type="criticality"
                      value={selectedAnalysis.isCritical ? t('dashboard:csv.critical') : t('dashboard:csv.simple')}
                    />
                  </Grid>
                  {selectedAnalysis.duration && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                          {t('dashboard:details.completedAt')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {selectedAnalysis.duration.completed_at}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
              {/*
              {selectedAnalysis.duration && (
                <Box sx={{ 
                  p: 3,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: 1, borderColor: 'divider'
                }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}> 
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Dauer
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {selectedAnalysis.duration.display}
                      </Typography>
                    </Grid>   
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Erstellt am
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {selectedAnalysis.duration.started_at}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Abgeschlossen
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {selectedAnalysis.duration.completed_at}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )} */}

              {/* Token-Kosten Aufschlüsselung */}
              {/*
              {selectedAnalysis.costDetails && (
                <Box sx={{ 
                  p: 3,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: 1, borderColor: 'divider'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                    Token-Kosten Aufschlüsselung
                  </Typography>
                  
                  
                  {selectedAnalysis.costDetails.pdf_conversion && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                        PDF Konvertierung
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Kosten
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {formatCostGerman(selectedAnalysis.costDetails.pdf_conversion.cost)} €
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Seiten
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {selectedAnalysis.costDetails.pdf_conversion.pages}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {selectedAnalysis.costDetails.ai_models && Object.entries(selectedAnalysis.costDetails.ai_models).map(([modelName, modelData]) => (
                    <Box key={modelName} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                        {modelName}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Prompt-Kosten
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {formatCostGerman(modelData.prompt_cost)} €
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {modelData.prompt_tokens.toLocaleString()} Tokens
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Completion-Kosten
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {formatCostGerman(modelData.completion_cost)} €
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {modelData.completion_tokens.toLocaleString()} Tokens
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Gesamt-Kosten
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#059669' }}>
                            {formatCostGerman(modelData.total_cost || (modelData.prompt_cost + (modelData.cached_cost || 0) + modelData.completion_cost))} €
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {modelData.total_tokens.toLocaleString()} Tokens
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            API-Requests
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {modelData.successful_requests}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )}
            */}
              {/* Download-Aktionen */}
              <Box sx={{
                p: 3,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: 1, borderColor: 'divider'
              }}>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleDownloadResults(selectedAnalysis.id)}
                      disabled={downloadingAnalysis === selectedAnalysis.id}
                      startIcon={downloadingAnalysis === selectedAnalysis.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                      data-testid="dashboard-analysis-detail-download-results-button"
                      sx={{ textTransform: 'none' }}
                    >
                      {t('dashboard:buttons.downloadResults')}
                    </Button>
                  </Grid>
                  {/*<Grid item xs={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleDownloadAddendum(selectedAnalysis.id)}
                      disabled={downloadingAnalysis === selectedAnalysis.id}
                      startIcon={downloadingAnalysis === selectedAnalysis.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                      data-testid="dashboard-analysis-detail-download-addendum-button"
                      sx={{ textTransform: 'none' }}
                    >
                      Ergänzungsvereinbarung
                    </Button>
                  </Grid> */}
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default DashboardSection; 